import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  HostListener
} from '@angular/core';
import {KeyValue} from '@angular/common';
import {fromEvent, Observable, Subscription} from 'rxjs';
import {document} from 'ngx-bootstrap/utils';
import {TableData} from './TableData.model';
import {GlobalConstants} from '../../common/global-constants';
import {BUTTON_STYLES, BUTTON_TYPES} from '../buttons/buttons.component';
import { SCANNABILITY_SCORE } from '../utils';

export enum EMPTY_STATE_TYPES {
  DEFAULT,
  ACTION_BUTTON_SUPPORT
}

enum TABLE_DATA_TYPES {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  ANALYTICS_TREND = 'trend',
  PROGRESS_BAR = 'progressbar'
}

@Component({
  selector: 'beaconstac-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnDestroy {
  checkboxOptions = {label: '', state : false, disabled: false};
  sortBy = 3;
  lastSortBy = 3;
  ordering = '-updated';
  ascending = false;
  innerWidth: any;
  mobileViewPortSize = GlobalConstants.MobileViewPortSize;
  SCANNABILITY_SCORE = SCANNABILITY_SCORE;
  isRowHovered: boolean[] = [];
  isTooltipHovered: boolean[] = [];

  /**
   * tableData
   *
   * @required
   */
  @Input()
  tableData: TableData;

  /**
   * Dropdowns
   *
   * @required
   */
  @Input()
  dropdowns = [];

  /**
   * pages Dropdown
   *
   * @required
   */
  @Input()
  pages = [];

  /**
   * showSearch: Show search box
   *
   * @required
   */
  @Input()
  searchBox = false;

  /**
   * showPagination: Show Pagination
   *
   * @required
   */
  @Input()
  pagination:number;

  /**
   * showPagination: Show Pagination
   *
   * @required
   */
  @Input()
  currentPage:number;

  /**
   * showPagination: Show Pagination
   *
   * @required
   */
  @Input()
  totalResults:number = 1;

  /**
   * selectOptions: Show Pagination
   *
   * @required
   */
  @Input()
  selectOptions: [];

  /**
   * showPages: Show Pages
   *
   * @required
   */
  @Input()
  showPages = false;

  /**
   * showToolbar: Show Toolbar
   *
   * @required
   */
  @Input()
  showToolBar = false;

  /**
   * showCheckbox: Show Checkbox
   *
   * @required
   */
  @Input()
  showCheckbox: boolean;

  @Input()
  truncateLabels: boolean;

  @Output()
  onClickMoreLabels = new EventEmitter<any>();

  @Input()
  isAscending: boolean;

  @Input()
  sortColumn: string;

  @Input()
  showLoadMore: boolean;

  @Output()
  loadMoreClicked = new EventEmitter<Event>();

  @Input()
  isTwoStepPagination: boolean;

  @Input()
  twoStepPaginationLastPage: boolean;

  @Input()
  twoStepPaginationFirstPage: boolean;

  @Input()
  searchBoxWidth = '238px';

  @Input()
  searchPlaceHolder: string;

  @Input()
  circularProfileImage: boolean;

  @Input()
  currentPageSize: number = 0;

  @Input()
  isLinkPrimary: boolean = true;

  @Input()
  showChartLegendColor: boolean;

  @Output()
  twoStepPaginationNext: EventEmitter<any> = new EventEmitter<Event>();

  @Output()
  twoStepPaginationPrev: EventEmitter<any> = new EventEmitter<Event>();

  @Output()
  onClick = new EventEmitter<Event>();

  @Output()
  onSort = new EventEmitter<Event>();

  @Output()
  sortColumnChange = new EventEmitter<string>();

  @Output()
  isAscendingChange = new EventEmitter<boolean>();

  @Output()
  onDropdownChange = new EventEmitter<Event>();

  @Output()
  onDropdownValueChange = new EventEmitter<string>();

  @Output()
  onShowPagesChange = new EventEmitter<any>();

  @Output()
  onPageChange = new EventEmitter<number>();

  @Output()
  onRowClick = new EventEmitter<any>();

  @Output()
  onToolbarClick = new EventEmitter<any>();

  @Output()
  onLinkClick = new EventEmitter<any>();

  @Output()
  onSearchBoxChange = new EventEmitter<any>();

  @Output()
  onToolbarDropdownChange = new EventEmitter<any>();

  @Output()
  onMobileToolbarDropdownChange = new EventEmitter<any>();

  @Output()
  checkboxStateEmitter = new EventEmitter<any>();

  @Output()
  allCheckboxStateEmitter = new EventEmitter<any>();

  @Output()
  onDropdownOpened = new EventEmitter<any>();

  @Input()
  fetchingData = false;

  @Input()
  showInbuiltEmptyState: boolean;

  @Input()
  emptyStateUrl = '';

  @Output()
  onInputValueChange = new EventEmitter<object>();

  @Output()
  fetchMoreData: EventEmitter<null> = new EventEmitter();

  @Input()
  isFetchingData = false;

  @Input()
  enableInfiniteScrolling = false;

  @Input()
  emptyStateType: EMPTY_STATE_TYPES = EMPTY_STATE_TYPES.DEFAULT;

  @Input()
  emptyStateTitle = 'No data to show';

  @Input()
  emptyStateDescription = 'Create a new item to get started';

  @Output()
  emptyStateButtonClick = new EventEmitter<any>();

  @Output()
  onCellClick = new EventEmitter<any>();

  searchValue: string;
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  pageSize: any = 10;
  dropdownStates: boolean[] = [];

  customImageStyles = {
    'border-radius': '50%',
    'min-width': '-webkit-fill-available'
  };

  BUTTON_TYPES = BUTTON_TYPES;
  BUTTON_STYLES = BUTTON_STYLES;
  EMPTY_STATE_TYPES = EMPTY_STATE_TYPES;

  constructor() {
  }

  ngOnInit() {
    this.innerWidth = window.innerWidth;
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
      this.innerWidth = evt['target']['innerWidth'];
    });
  }

  updatePagination(event) {
    this.pageSize = event.value
    this.pagination = Math.ceil(this.totalResults / event.value);
  }

  handleSortIcon(by: number, sortColumn): void {
    this.sortColumn = sortColumn;
    if (this.lastSortBy === by) {
      this.ascending = !this.ascending;
    } else {
      this.ascending = true;
    }
    this.sortBy = by;
    this.lastSortBy = this.sortBy;
  }

  handleMore(event) {
    const dropdown = event.target.nextSibling;
    if (event.target && event.target.nextSibling) {
      dropdown.classList.toggle('active');
    }
  }

  // Preserve original property order
  originalOrder = (a: KeyValue<number,string>, b: KeyValue<number,string>): number => {
    return 0;
  };

  checkAllCheckboxes(event) {
    const checkboxes = document.getElementsByTagName('input');
    const element = event.target;
    if (element.checked) {
      for (let i = 0; i < checkboxes.length; i++) {
        if (checkboxes[i].type == 'checkbox') {
          checkboxes[i].checked = true;
        }
      }
    } else {
      for (let i = 0; i < checkboxes.length; i++) {
        if (checkboxes[i].type == 'checkbox') {
          checkboxes[i].checked = false;
        }
      }
    }
  }

  toggleDropdowns(event) {
    // const element = event.target;
    const panel = document.getElementById('accordian');
    panel.classList.toggle('active');
    if (panel.style.maxHeight) {
      panel.style.maxHeight = null;
    } else {
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }
  }

  hasMultipleValues(type) {
    return type === TABLE_DATA_TYPES.MULTIPLE;
  }

  hasSingleValue(type) {
    return type === TABLE_DATA_TYPES.SINGLE;
  }

  isTrendType(type) {
    return type === TABLE_DATA_TYPES.ANALYTICS_TREND;
  }

  isProgressBar(type) {
    return type === TABLE_DATA_TYPES.PROGRESS_BAR;
  }

  ngOnDestroy() {
    this.resizeSubscription$.unsubscribe();
  }

  searchTextChange(event) {
    this.searchValue = event.toString();
    this.onSearchBoxChange.emit(event)
  }

  onDropdownSearchInputChange(value, indexOfDropdown){
    const valueToEmit = {
      "value": value,
      "indexOfDropdown": indexOfDropdown
    };

    this.onInputValueChange.emit(valueToEmit);
  }

  fetchDataOnScroll(event) {
    this.fetchMoreData.emit();
  }

  handleOnCellClick(dataItem, dataObject) {
    this.onCellClick.emit({
      dataItem,
      dataObject
    });
  }

  toggleDropdown(rowIdx: number, data?): void {
    const currentState = this.dropdownStates[rowIdx];
    this.dropdownStates = this.dropdownStates.map(() => false);
    if (!currentState) {
      this.onDropdownOpened.emit({rowIndex: rowIdx, dataObject: data});
      this.dropdownStates[rowIdx] = true;
    }
  }

  handleClickOnMoreLabels(event,dataItem){
    event.stopPropagation();
    this.onClickMoreLabels?.emit({
      event,
      dataItem
    });
  }

  qrHasLowScannability(listItem): boolean {
    const scanScore = listItem?.dataObject?.isScanable?.scanScore;
    return scanScore === SCANNABILITY_SCORE.UNSCANNABLE || scanScore === SCANNABILITY_SCORE.POOR;
  }

  checkTooltipHover(tooltipIndex: number, state: boolean) {
    this.isTooltipHovered[tooltipIndex] = state;
  }

  checkRowHover(rowIndex: number, state: boolean) {
    this.isRowHovered[rowIndex] = state;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    if (!targetElement.closest('.dropdown') && !targetElement.closest('.fa-ellipsis-v')) {
      Object.keys(this.dropdownStates).forEach(key => {
        this.dropdownStates[key] = false;
      });
      }
  }

}
